import { PlayerId } from "../../types";
import React, { useMemo } from "react";
import Lobby from "./lobby/lobby";
import QuickStart from "./quick-start/quick-start";
import CharacterSubmit from "./character-submit/character-submit";

import OtpSubmit from "./character-submit/otp-submit";
import CreateStory from "./create-story/create-story";
import ReadingTime from "./game-voting/reading-time";
import GameResults from "./game-results/game-results";
import StoryCensor from "./story-title/story-censor";
import { useAppSelector } from "../../redux/store";
import { GameContext } from "../../contexts";
import StoryTitle from "./story-title/story-title";
import Break from "./story-title/break";
import GamePage from "./game-page/game-page";
import Story from "./story-time/story";
import Minors from "./minors/minors";
import {
  selectGameStatus,
  selectGameSettings,
  selectPlayerStatus,
} from "../../redux/selectors";
import { useGameManager } from "../../hooks/useGameManager";
import { GameStatus } from "../../types/game-status";

type Props = {
  playerId: PlayerId;
};

const gameComponentMap: Partial<Record<GameStatus, React.ElementType>> = {
  [GameStatus.GameStatusLobby]: Lobby,
  [GameStatus.GameStatusPostLobby]: Lobby,
  [GameStatus.GameStatusPreQuickStart]: QuickStart,
  [GameStatus.GameStatusQuickStart]: QuickStart,
  [GameStatus.GameStatusPostQuickStart]: QuickStart,
  [GameStatus.GameStatusMinors]: Minors,
  [GameStatus.GameStatusPostMinors]: Minors,
  [GameStatus.GameStatusPreStoryCreate]: CreateStory,
  [GameStatus.GameStatusStoryCreate]: CreateStory,
  [GameStatus.GameStatusPostStoryCreate]: CreateStory,
  [GameStatus.GameStatusPreStoryWrite]: Story,
  [GameStatus.GameStatusStoryWriteTransition]: Story,
  [GameStatus.GameStatusStoryWrite]: Story,
  [GameStatus.GameStatusPostStoryWrite]: Break,
  [GameStatus.GameStatusPreStoryTitle]: StoryTitle,
  [GameStatus.GameStatusStoryTitle]: StoryTitle,
  [GameStatus.GameStatusPostStoryTitle]: StoryTitle,
  [GameStatus.GameStatusPreCensoring]: StoryCensor,
  [GameStatus.GameStatusCensoring]: StoryCensor,
  [GameStatus.GameStatusPostCensoring]: StoryCensor,
  [GameStatus.GameStatusPreVoting]: ReadingTime,
  [GameStatus.GameStatusReacting]: ReadingTime,
  [GameStatus.GameStatusVoting]: ReadingTime,
  [GameStatus.GameStatusPostReading]: ReadingTime,
  [GameStatus.GameStatusResults]: GameResults,
  [GameStatus.GameStatusPreResults]: GameResults,
};

// Stable fallback page components - defined outside component to maintain identity
const InvalidStatePage = () => <GamePage titles={["Invalid State"]} />;
const PreparingGamePage = () => <GamePage titles={["Getting game ready..."]} />;
const LoadingPage = () => <GamePage titles={["Loading..."]} />;


const GameManager = ({ playerId }: Props) => {
  const gameStatus = useAppSelector(selectGameStatus);
  const gameSettings = useAppSelector(selectGameSettings);
  const playerStatus = useAppSelector(selectPlayerStatus);

  const { sendMessage } = useGameManager(playerId);

  // Memoize context value to prevent unnecessary rerenders of consumers
  const contextValue = useMemo(
    () => ({ submit: sendMessage }),
    [sendMessage]
  );

  // Determine which component to render based on game state
  // Return component references, not inline arrow functions, to maintain stable identity
  const GamePhaseComponent = useMemo(() => {
    if (playerStatus === GameStatus.GameStatusInvalid) {
      return InvalidStatePage;
    }

    switch (gameStatus) {
      case GameStatus.GameStatusCharacters:
      case GameStatus.GameStatusPostCharacters:
        if (
          gameSettings.otp_mode ||
          (gameSettings.solo_writing &&
            !gameSettings.self_shuffle)
        ) {
          return OtpSubmit;
        }
        return CharacterSubmit;

      case GameStatus.GameStatusPreCharacters:
        return PreparingGamePage;

      default:
        return gameComponentMap[gameStatus] || LoadingPage;
    }
  }, [gameStatus, playerStatus, gameSettings]);

  return (
    <GameContext.Provider value={contextValue}>
      <GamePhaseComponent />
    </GameContext.Provider>
  );
};

export default GameManager;
